/**
 * overlayPlacement — THE PLACEMENT ENGINE. Pure over its inputs.
 *
 * One function answers "where does this overlay go" for every kind the roster
 * marks `placed`, on the `@floating-ui/dom` copy floating-vue already installs.
 * `overlayKinds.js` says WHICH middleware each kind gets; this module says in
 * what ORDER they run and what the caller is handed back. `useOverlay.js`
 * (th-4vkxa.6) owns the lifecycle — when to place, when to re-place, what to do
 * with the result — and nothing about the lifecycle lives here.
 *
 * ── THE MIDDLEWARE ORDER IS FIXED, AND THE ORDER IS THE POINT ───────────────
 *
 *   offset → flip → shift → arrow → avoidOverlays → size (WIDTH only)
 *
 * `size` runs LAST because it reads `middlewareData.shift` to decide whether the
 * available box is the clipped one or the shifted one, and because its
 * `reset: { rects: true }` re-runs every middleware ahead of it — a `size` in
 * the middle would run the chain twice for nothing. `avoidOverlays` runs just
 * before it so the rect `size` measures is the AVOIDED rect, not the one
 * avoidance was about to move. `arrow` sits after `shift` because an arrow
 * points at the reference from wherever the box finally landed. `shift` takes
 * floating-ui's `crossAxis` — the axis the PLACEMENT runs along, the vertical
 * one for a top/bottom side, off by default — exactly when `flip` is dropped,
 * because the flip is what answers an overflow on that axis for every other
 * kind and a held cursor card has no flip to answer it with: without this the
 * card that grew past the viewport bottom would sit there unmoved.
 *
 * THE MAIN-AXIS OFFSET IS PER SIDE. `offset` is handed a FUNCTION of the
 * middleware state rather than a number, because a kind's `flipClearance` is the
 * EXTRA clearance the FLIPPED side takes on top of its `gap` — the Skyline lane
 * card sits 8 px below the lane and 20 px above it — and the flipped side is the
 * side OPPOSITE the placement the call asked for. The `offset` slot can answer
 * that precisely because `flip` returns `reset: { placement }`, which restarts
 * the chain at `offset` with the candidate placement, so the function is
 * evaluated once per CANDIDATE rather than once per call. One roster option,
 * two readers: `laneBudget` subtracts `flipClearance` from the room above and
 * the placement adds it to the offset there, so the budget can never promise
 * room the placement then spends. A kind that declares no `flipClearance` takes
 * 0 on both sides, which is the bare `offset(gap)` it had before.
 *
 * A KIND MAY BIND ITS OFFSETS TO THE SCREEN AXES INSTEAD. `gap` and `crossGap`
 * are PLACEMENT-bound: floating-ui hands `mainAxis` to whichever screen axis
 * the placement runs along, so a card that sidesteps from `bottom-start` onto
 * `right` takes its VERTICAL gap horizontally. Where a kind's two clearances
 * mean different things that is wrong, not merely different — the cursor card
 * owes the bow glyph `BOW_CURSOR_CARD_CLEARANCE_X` on X and 10 px on Y, and a
 * sidestep spending 10 px on X parks the card under the badge the clearance
 * exists to clear. Such a kind declares `axisOffsets: { x, y }`, which the
 * engine reads as written: a top/bottom side takes `y` on the main axis and `x`
 * on the cross axis (the numbers `gap`/`crossGap` gave it), and a left/right
 * side takes `x` on the main axis and NOTHING on the cross axis, because
 * floating-ui's base `right`/`left` placement already centres the box on the
 * reference's cross axis — against a 0×0 pointer rect that IS the retired
 * adapter's `y = cursorY − height / 2`, and any cross offset would push it back
 * off-centre. The two vocabularies are exclusive and `axisOffsets` wins where a
 * kind declares it; `flipClearance` is owed on the flipped side under both.
 *
 * ── THE HEIGHT BUDGET IS A PRE-PASS, NEVER `size` ──────────────────────────
 *
 * `size.availableHeight` is measured for the side floating-ui CHOSE, after the
 * flip — so using it to bound the overlay is circular: the height decides the
 * flip and the flip decides the height. That circularity is a shipped defect
 * with a date on it (2026-08-14: a viewport-tall lane card that fit neither side,
 * flipped negative and covered the whole Skyline pane). So `budget` is a PURE
 * function of the reference rect and the viewport, run BEFORE `computePosition`,
 * and `size` is used for WIDTH and nothing else. A budget that cannot be
 * computed is `null`, which means UNBOUNDED — a momentarily over-tall overlay
 * beats one collapsed to nothing by a measurement taken before layout settled.
 *
 * THE CAP THEN NARROWS TO THE SIDE THE PLACEMENT CHOSE. The pre-pass bounds the
 * box before the flip, so it cannot know the side; once the chain has chosen
 * one, the engine asks the same pure budget again WITH that side, and
 * `roomierSideBudget` answers that side's own room. At the roomier side's room,
 * a box the flip puts below a low anchor can grow past the viewport bottom on
 * the room ABOVE it, and a box an obstacle sends below (capped at the room
 * below) re-grows to the roomier cap on the next pass, flips back into the
 * obstacle and is sent below again — each pass undoing the last. Narrowed, a
 * pass over the box its own cap produced lands exactly where that box stands.
 * `laneBudget`, `viewportBudget` and a call site's own function take no side and
 * answer the same number both times (the lane card's spec pins the roomier side
 * of the lane).
 *
 * A READABLE MINIMUM DECIDES THE SIDE BEFORE THE CHAIN RUNS. A kind that
 * declares `readableHeight` (the Peek, founder ruling 26) is not flipped on its
 * measured height. `readableSide` decides its side from the anchor and the
 * viewport alone: the side the call asked for, unless that side's room cannot
 * hold the readable height and the opposite side's room is larger. The pre-pass
 * then caps the box at the DECIDED side's room instead of the roomier one, the
 * flip may change only the alignment on that side, and the narrowed cap is the
 * same number. A box measured short and a box measured tall therefore land on
 * the same side, and a body that loads after the first frame can never move it;
 * only a moved anchor asks again. `avoidOverlays` applies the same rule to the
 * room left clear of obstacles (see its header).
 *
 * ── NULL WHEN UNMEASURED ───────────────────────────────────────────────────
 *
 * A floating box that measures 0×0 has not been laid out yet. `placeOverlay`
 * returns `null` for it rather than a coordinate: the adopter renders the
 * surface UNPOSITIONED, never pinned to the origin, and places it on the next
 * pass. That is why the caller places twice — once on open, once after
 * `nextTick` — and that two-pass belongs to `useOverlay.js`, not here.
 *
 * ── "SYNCHRONOUSLY ON OPEN" MEANS NO FRAME WAIT, NOT NO PROMISE ────────────
 *
 * `computePosition` is async in the installed 1.1.1 (its platform's
 * `getElementRects` is an async method), so `placeOverlay` is async too and
 * resolves in a MICROTASK — no `requestAnimationFrame`, no timer, nothing that
 * waits for a paint. The design's "runs synchronously on open" is that
 * guarantee: the placement is computed immediately, in the same tick's
 * microtask drain.
 *
 * ── THE TRANSFORMED-ANCESTOR RULE ──────────────────────────────────────────
 *
 * 1.1.1's `getOffsetParent` is NOT strategy-aware: a `transform`,
 * `will-change` or `filter` ancestor of the FLOATING node becomes its offset
 * parent even under `strategy: 'fixed'`, and the returned coordinates are then
 * relative to that ancestor rather than to the viewport. So every node this
 * engine places is either portaled to `body` or is verified to sit under no
 * transformed ancestor. The roster's header carries the same rule; the adapters
 * that render inline (the cursor adapters, by design) are the ones it is
 * checked against.
 *
 * ── THE VIEWPORT IS READ EXACTLY AS FLOATING-UI READS IT ───────────────────
 *
 * `viewportRect` mirrors 1.1.1's own `getViewportRect`: `window.visualViewport`
 * when it exists, else `document.documentElement.clientWidth/clientHeight`.
 * One source, so the budget can never promise room the placement does not have.
 * In jsdom there is no `visualViewport` and the document element reports 0, so a
 * spec stubs `documentElement.clientWidth/clientHeight` — a 0 viewport degrades
 * to an UNBOUNDED budget, which is the safe direction.
 *
 * ── A BOUNDARY NARROWS THE AREA TO ONE ELEMENT'S BOX ──────────────────────
 *
 * By default an overlay is placed inside the viewport. A call that passes the
 * `boundary` option — a GETTER answering an Element, read on every pass — is
 * placed inside that element's box instead (where it lies inside the viewport):
 * the map popups are kept inside the map container, so a point near the map's
 * edge flips and shifts its popup inward and never pans the map to fit it
 * (v2-map-platform D11a). It is floating-ui's own `boundary`, handed to `flip`,
 * `shift` and `size`, and `placementArea` hands the budget the same box, so the
 * cap never promises room the placement does not have. A getter answering no
 * element (no map mounted) is the viewport. There is ONE such option: the map
 * popups and the map glance card read it alike.
 *
 * ── MEASURING THE FLOATING BOX IN JSDOM ────────────────────────────────────
 *
 * 1.1.1 measures the floating element through `getComputedStyle` with an
 * `offsetWidth`/`offsetHeight` fallback, never through `getBoundingClientRect`.
 * jsdom returns 0 for the offset properties, so the unmeasured check reads them
 * first (the same numbers the engine will use) and falls back to the client rect
 * only when both are 0 — which is what lets a spec stub either one.
 * `~test/helpers/floatingDimensions` stubs both.
 *
 * ── THE ARROW IS A CUSTOM MIDDLEWARE, NOT FLOATING-UI'S ────────────────────
 *
 * floating-ui's `arrow({ element })` MEASURES a DOM node. The one kind that
 * carries an arrow — the Skyline lane card — has no arrow node: the arrow is a
 * pair of props on `EntityGlanceTooltip` (`arrow-placement` and a Number
 * `arrow-offset`), drawn by the card itself, and `arrowHalf` is half the
 * triangle's width. So the slot holds a small middleware of our own that reports
 * the point the arrow must aim at instead of positioning an element.
 */

import { computePosition, offset, flip, shift, size, autoUpdate } from "@floating-ui/dom";

import { heightBudgetFor } from "@/components/glance/glanceCardHeightBudget";

import { avoidOverlays } from "./avoidOverlays";
import { kindFor } from "./overlayKinds";

/** The placement used when neither the call site nor the roster names one. */
const DEFAULT_PLACEMENT = "bottom-start";

const NOOP = () => {};

// ---------------------------------------------------------------------------
// THE VIEWPORT
// ---------------------------------------------------------------------------

/**
 * The viewport rect, read the way `@floating-ui/dom` 1.1.1 reads it.
 *
 * @param {Element|null} [node] - any node whose document/window to measure;
 *   defaults to the ambient `document`.
 * @returns {{ width: number, height: number, top: number, left: number,
 *   right: number, bottom: number }} zeroes when nothing is measurable.
 */
export function viewportRect(node) {
  const doc = node?.ownerDocument ?? (typeof document === "undefined" ? null : document);
  const win = doc?.defaultView ?? (typeof window === "undefined" ? null : window);
  const html = doc?.documentElement;

  const visual = win?.visualViewport;
  const width = visual ? visual.width : (html?.clientWidth ?? 0);
  const height = visual ? visual.height : (html?.clientHeight ?? 0);

  return {
    width: width > 0 ? width : 0,
    height: height > 0 ? height : 0,
    top: 0,
    left: 0,
    right: width > 0 ? width : 0,
    bottom: height > 0 ? height : 0,
  };
}

/**
 * The element a call's `boundary` getter answers, or `null` for none (see the
 * header). Read on every pass, so a container that mounts later is picked up.
 *
 * @param {{ boundary?: () => (Element|null|undefined) }} [options]
 * @returns {Element|null}
 */
function boundaryOf(options) {
  const element = typeof options?.boundary === "function" ? options.boundary() : null;
  return isElementNode(element) ? element : null;
}

/**
 * The area a placement is bounded by: the viewport, or, for a call naming a
 * `boundary`, the part of that element's box inside it (see the header). The
 * element's box is its INNER box, read exactly as floating-ui reads a boundary
 * (`getInnerBoundingClientRect`): its client rect inset by its border, at its
 * client size. A boundary that measures nothing leaves the viewport, as an
 * unmeasured viewport leaves the boundary alone.
 *
 * @param {Element|null} [node] - any node whose document/window to measure.
 * @param {{ boundary?: () => (Element|null|undefined) }} [options] - the merged
 *   kind + call-site options.
 * @returns {{ width: number, height: number, top: number, left: number,
 *   right: number, bottom: number }} the shape `viewportRect` answers.
 */
export function placementArea(node, options) {
  const viewport = viewportRect(node);
  const boundary = boundaryOf(options);
  const rect = boundary?.getBoundingClientRect();
  if (!rect) return viewport;

  const width = boundary.clientWidth || rect.width;
  const height = boundary.clientHeight || rect.height;
  if (!(width > 0) || !(height > 0)) return viewport;

  const inner = {
    top: rect.top + (boundary.clientTop || 0),
    left: rect.left + (boundary.clientLeft || 0),
  };
  inner.right = inner.left + width;
  inner.bottom = inner.top + height;

  const measured = viewport.width > 0 && viewport.height > 0;
  const top = measured ? Math.max(inner.top, viewport.top) : inner.top;
  const left = measured ? Math.max(inner.left, viewport.left) : inner.left;
  const right = measured ? Math.min(inner.right, viewport.right) : inner.right;
  const bottom = measured ? Math.min(inner.bottom, viewport.bottom) : inner.bottom;
  return {
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
    top,
    left,
    right,
    bottom,
  };
}

// ---------------------------------------------------------------------------
// A REFERENCE AT A POINT
// ---------------------------------------------------------------------------

/**
 * A VIRTUAL element at a point — floating-ui's `{ getBoundingClientRect }` with
 * no node behind it — whose rect is 0×0 at whatever `getPoint()` returns. The
 * engine anchors to it exactly as it anchors to a node, and both vertical edges
 * of a zero-height rect are the point itself, which is what lets a budget read
 * `top` and `bottom` against it.
 *
 * THE POINT IS READ ON EVERY CALL, so one helper serves both pointer anchors.
 * A LIVE anchor (the bow badge, the dwell ring, the floating input's re-aim)
 * passes a getter over the moving position and keeps this ONE reference for its
 * life: `useOverlay` re-attaches on a reference whose identity changes, so a
 * moving point asks for `replace()` rather than handing over a new object. A
 * FROZEN anchor (the cursor card's reveal rect, the lane point) passes a getter
 * over a point it captured, and captures again by building a NEW reference —
 * which is exactly the identity change that re-attaches.
 *
 * A missing point, or a missing coordinate, reads as 0. An adopter that must not
 * paint at an unknown point gates its `open` on a known one, so the origin is
 * never what a placed overlay is measured against.
 *
 * @param {() => ({ x?: number, y?: number }|null|undefined)} getPoint
 * @returns {Readonly<{ getBoundingClientRect: () => { x: number, y: number,
 *   top: number, left: number, right: number, bottom: number, width: 0,
 *   height: 0 } }>}
 */
export function pointReference(getPoint) {
  return Object.freeze({
    getBoundingClientRect: () => {
      const point = getPoint();
      const x = point?.x ?? 0;
      const y = point?.y ?? 0;
      return { x, y, top: y, left: x, right: x, bottom: y, width: 0, height: 0 };
    },
  });
}

// ---------------------------------------------------------------------------
// THE BUDGET PRE-PASS
//
// A `budget` is `(referenceRect, viewport, options, side) => number | null`. The
// pre-pass asks it with no `side`, or with the side `readableSide` decided for a
// kind that declares a readable minimum; once the chain has placed, the engine
// asks again with the side it chose (see the header), and a budget that does not
// depend on the side ignores the argument. The three below are the ones the
// roster's kinds default to; a call site may pass its own (the map popup
// budgets against its container, not the viewport). The per-kind DEFAULT wiring
// lives in `useOverlay.js`, not here — this module takes the function it is given.
// ---------------------------------------------------------------------------

/**
 * The vertical edges of an area: its own, or `0` and its height for an area
 * given as a bare `{ height }` (the viewport's, whose top is 0).
 */
function areaEdges(area) {
  const top = Number.isFinite(area.top) ? area.top : 0;
  const bottom = Number.isFinite(area.bottom) ? area.bottom : top + area.height;
  return { top, bottom };
}

/**
 * The room on one vertical side of the reference, raw: the side the placement
 * chose, and before it has chosen one, the ROOMIER side.
 *
 * ← the retired `inspectorPanelPlacement`
 * (`components/universal/elementInspectorLayer.js`, removed in th-e692n.1):
 * `roomBelow = viewport.height − anchor.bottom − gap − padding`,
 * `roomAbove = anchor.top − gap − padding`. The pre-pass bounds the unplaced box
 * by the roomier of the two, so it always fits one side and the flip decides
 * against the height that will paint; the placed box is then capped at what the
 * side it landed on actually has, so a panel taller than its room scrolls INSIDE
 * itself rather than running off-screen.
 *
 * The rooms are measured to the AREA's edges (`placementArea`): the viewport's,
 * whose top is 0, or a boundary's. An area given as a bare `{ height }` is the
 * viewport's.
 *
 * @param {DOMRect|object} referenceRect
 * @param {{ height: number, top?: number, bottom?: number }} viewport - the area.
 * @param {{ gap?: number, padding?: number }} [options]
 * @param {string|null} [side] - the chosen side. `'bottom'` and `'top'` answer
 *   their own room; no side (the pre-pass) or a horizontal one answers the
 *   roomier vertical room.
 * @returns {number} may be non-positive, which `computeBudget` reads as unbounded.
 */
export function roomierSideBudget(referenceRect, viewport, { gap = 0, padding = 0 } = {}, side = null) {
  const { top, bottom } = areaEdges(viewport);
  const roomBelow = bottom - referenceRect.bottom - gap - padding;
  const roomAbove = referenceRect.top - top - gap - padding;
  if (side === "bottom") return roomBelow;
  if (side === "top") return roomAbove;
  return Math.max(roomBelow, roomAbove);
}

/**
 * The Skyline metadata lane's budget: ONE SIDE of the lane, the roomier one,
 * through `heightBudgetFor`.
 *
 * ← `MetadataLaneTooltip.vue`'s `roomBelowAnchor` / `roomAboveAnchor`:
 * `roomBelow = viewport.height − (y + gap) − padding`,
 * `roomAbove = y − gap − flipClearance − padding`, where `y` is the arrow point.
 * The lane's reference is a 0×0 rect AT that point, so `referenceRect.bottom`
 * and `referenceRect.top` are both `y` and the transcription is exact; reading
 * the rect's two edges rather than one number is what generalises it to a
 * reference with height.
 *
 * THE VIEWPORT FALLBACK IS THE SHIPPED BEHAVIOUR, kept deliberately: the
 * component falls back to `heightBudgetFor(viewportHeight)` when NEITHER side of
 * the lane has usable room (an off-screen anchor, or a viewport shorter than the
 * gaps), because a momentarily over-tall card beats the unbounded card that
 * `heightBudgetFor`'s `null` would mean there. Dropping it would be a silent
 * behaviour change in the exact case the 2026-08-14 defect lived in.
 *
 * @param {DOMRect|object} referenceRect
 * @param {{ height: number }} viewport
 * @param {{ gap?: number, padding?: number, flipClearance?: number }} [options]
 * @returns {number|null} `null` = unbounded.
 */
export function laneBudget(
  referenceRect,
  viewport,
  { gap = 0, padding = 0, flipClearance = 0 } = {},
) {
  const { top, bottom } = areaEdges(viewport);
  const roomBelow = bottom - (referenceRect.bottom + gap) - padding;
  const roomAbove = referenceRect.top - top - gap - flipClearance - padding;
  return heightBudgetFor(Math.max(roomBelow, roomAbove)) ?? heightBudgetFor(viewport.height);
}

/**
 * The whole viewport, through `heightBudgetFor` — the cursor card's budget,
 * which is confined by nothing narrower than the box it is `fixed` in.
 *
 * @param {DOMRect|object} _referenceRect - unused; a budget's signature is uniform.
 * @param {{ height: number }} viewport
 * @returns {number|null} `null` = unbounded.
 */
export function viewportBudget(_referenceRect, viewport) {
  return heightBudgetFor(viewport.height);
}

/**
 * Runs the budget pre-pass and normalises its answer.
 *
 * A non-finite or non-positive budget is UNBOUNDED (`null`), never a clamp to
 * zero: the only thing a 0 px cap can produce is a collapsed overlay, and every
 * case that reaches it here is a measurement that has not settled.
 *
 * @param {object} config
 * @param {DOMRect|object} config.referenceRect
 * @param {{ height: number, width: number }} config.viewport
 * @param {Function|null} [config.budget] - `(referenceRect, viewport, options, side) => number | null`.
 * @param {object} [config.options] - the merged kind + call-site options.
 * @param {string|null} [config.side] - the side the placement chose; absent for the pre-pass.
 * @param {string|null} [config.placement] - the placement the call asked for. The
 *   pre-pass of a kind with a readable minimum asks for the side `readableSide`
 *   decides from it.
 * @returns {number|null} the `maxHeight`, or `null` for unbounded.
 */
export function computeBudget({
  referenceRect,
  viewport,
  budget,
  options = {},
  side = null,
  placement = null,
} = {}) {
  if (typeof budget !== "function") return null;
  if (!referenceRect || !viewport) return null;

  const asked = side ?? readableSide(referenceRect, viewport, options, placement);
  const value = budget(referenceRect, viewport, options, asked);
  return Number.isFinite(value) && value > 0 ? value : null;
}

// ---------------------------------------------------------------------------
// THE READABLE SIDE
//
// A kind that declares `readableHeight` has its side decided here, before the
// chain runs, and never by its measured height (see the header).
// ---------------------------------------------------------------------------

/**
 * The readable rule, over the room on two sides of an anchor: the side a box
 * stands on yields to the opposite side only when its own room cannot hold the
 * readable height AND the opposite side has more room. A cramped side beats a
 * side that is more cramped still, and a tie keeps the side it has. Two readers:
 * `readableSide` asks it of the viewport's room before the chain runs, and
 * `avoidOverlays` asks it of the room left clear of obstacles.
 *
 * @param {number} room - the room on the side the box stands on.
 * @param {number} oppositeRoom - the room on the other side of the anchor.
 * @param {number} readableHeight - the least height the surface reads at.
 * @returns {boolean}
 */
export function yieldsToOpposite(room, oppositeRoom, readableHeight) {
  return room < readableHeight && oppositeRoom > room;
}

/**
 * The side a kind with a readable minimum is placed on, decided from the anchor
 * and the viewport alone (founder ruling 26): the vertical side the placement
 * asks for, unless `yieldsToOpposite` sends it across. The rooms are
 * `roomierSideBudget`'s, so the cap the pre-pass binds is exactly the room on the
 * side decided here.
 *
 * Nothing about the box enters it, which is what makes the side sticky: every
 * pass over a still anchor answers the same, however far the body has loaded.
 *
 * @param {DOMRect|object} referenceRect
 * @param {{ height: number }} viewport
 * @param {{ readableHeight?: number, gap?: number, padding?: number }} [options]
 * @param {string|null} [placement] - the placement asked for; the engine's default when absent.
 * @returns {'top'|'bottom'|null} `null` for a kind that declares no readable
 *   minimum, or a placement on a horizontal side: the flip decides those.
 */
export function readableSide(referenceRect, viewport, options = {}, placement = null) {
  const { readableHeight } = options;
  if (!(Number.isFinite(readableHeight) && readableHeight > 0)) return null;

  const primary = sideOf(placement ?? DEFAULT_PLACEMENT);
  if (primary !== "top" && primary !== "bottom") return null;

  const opposite = OPPOSITE_SIDE[primary];
  const room = (side) => roomierSideBudget(referenceRect, viewport, options, side);
  return yieldsToOpposite(room(primary), room(opposite), readableHeight) ? opposite : primary;
}

/** The same placement on another side, its alignment kept: `bottom-start` → `top-start`. */
function onSide(placement, side) {
  const [, alignment] = String(placement).split("-");
  return alignment ? `${side}-${alignment}` : side;
}

/**
 * The other alignment on the same side, or `null` for a centred placement:
 * `top-start` → `top-end`.
 */
function otherAlignment(placement) {
  const [side, alignment] = String(placement).split("-");
  if (!alignment) return null;
  return `${side}-${alignment === "start" ? "end" : "start"}`;
}

// ---------------------------------------------------------------------------
// THE ARROW
// ---------------------------------------------------------------------------

/**
 * Where the arrow's tip sits: the reference's centre, in the floating box's own
 * coordinates (0 = its left edge), clamped so a triangle of half-width
 * `arrowHalf` stays inside the box.
 *
 * One function, called twice on purpose — once inside the middleware, so
 * `middlewareData.arrow` exists in the pinned order, and once against the FINAL
 * `x` after the chain, because `avoidOverlays` runs AFTER `arrow` and may move
 * the box out from under the mid-chain answer. The returned `arrow` is always
 * the final one.
 */
function arrowXFor({ referenceCenterX, floatingLeft, floatingWidth, arrowHalf }) {
  const raw = referenceCenterX - floatingLeft;
  const min = arrowHalf;
  const max = floatingWidth - arrowHalf;
  if (!(max >= min)) return floatingWidth / 2;
  return Math.min(Math.max(raw, min), max);
}

/**
 * The `arrow` slot in the fixed order. Reports a POINT rather than positioning
 * an element, because the one kind that carries an arrow draws it itself.
 */
function arrowMiddleware({ arrowHalf = 0 } = {}) {
  return {
    name: "arrow",
    options: { arrowHalf },
    fn({ x, rects }) {
      const referenceCenterX = rects.reference.x + rects.reference.width / 2;
      const floatingWidth = rects.floating.width;
      return {
        // The two measurements travel WITH the answer so the final re-clamp
        // below reuses floating-ui's own rects rather than re-measuring the DOM.
        data: {
          x: arrowXFor({ referenceCenterX, floatingLeft: x, floatingWidth, arrowHalf }),
          referenceCenterX,
          floatingWidth,
        },
      };
    },
  };
}

// ---------------------------------------------------------------------------
// PLACEMENT
// ---------------------------------------------------------------------------

/** The roster entry, whether the caller passed the entry or its name. */
function resolveKind(kind) {
  if (!kind) return null;
  return typeof kind === "string" ? kindFor(kind) : kind;
}

/**
 * The floating box's size, measured the way the engine measures it: offsets
 * first, the client rect only when both offsets are 0.
 */
function floatingBox(floating) {
  const offsetWidth = floating.offsetWidth ?? 0;
  const offsetHeight = floating.offsetHeight ?? 0;
  if (offsetWidth > 0 || offsetHeight > 0) return { width: offsetWidth, height: offsetHeight };

  const rect = floating.getBoundingClientRect?.();
  return { width: rect?.width ?? 0, height: rect?.height ?? 0 };
}

/** The side token (`top` | `right` | `bottom` | `left`) of a floating-ui placement. */
function sideOf(placement) {
  return String(placement).split("-")[0];
}

/**
 * The side a flip lands on, per side: the FLIPPED side of a placement is the
 * opposite one, which is exactly the fallback `flip` reaches for first when the
 * primary side cannot hold the box. A kind's `flipClearance` is owed on that
 * side and no other — a sidestep onto a cross axis is not a flip.
 */
const OPPOSITE_SIDE = Object.freeze({
  top: "bottom",
  bottom: "top",
  left: "right",
  right: "left",
});

/**
 * A kind's AXIS-bound clearances, or `null` when it takes the placement-bound
 * `gap`/`crossGap` pair instead (see the header). A half-declared `axisOffsets`
 * reads its missing axis as 0 rather than falling back to `gap`: mixing the two
 * vocabularies inside one kind is the ambiguity the split exists to remove.
 *
 * @param {object} options - the merged kind + call-site options.
 * @returns {{ x: number, y: number }|null}
 */
function axisOffsetsOf(options) {
  const declared = options.axisOffsets;
  if (!declared || typeof declared !== "object") return null;
  return {
    x: Number.isFinite(declared.x) ? declared.x : 0,
    y: Number.isFinite(declared.y) ? declared.y : 0,
  };
}

/**
 * Places one overlay.
 *
 * @param {object} config
 * @param {Element|{ getBoundingClientRect: Function, contextElement?: Element }} config.reference
 *   an element, or a VIRTUAL element — a reveal-captured rect, a pointer point
 *   (both `pointReference`), the zero-size probe a portaled panel leaves in its
 *   host's flow.
 * @param {Element} config.floating - the node being placed.
 * @param {object|string} config.kind - a roster entry, or the name of one.
 * @param {string} [config.placement] - overrides the kind's primary placement. A
 *   kind with a readable minimum may be placed on its opposite side (`readableSide`).
 * @param {Function|null} [config.budget] - the pre-pass (see `computeBudget`).
 * @param {() => DOMRect[]} [config.obstacles] - handed to `avoidOverlays`.
 * @param {object} [config.options] - merged OVER the kind's own options. `flip:
 *   false` drops the flip middleware entirely, which is how a held cursor card
 *   re-clamps without mirroring itself across a pointer that froze long ago.
 *   `boundary` (a getter answering an Element) places the box inside that
 *   element's box rather than the viewport (see the header).
 * @returns {Promise<null|{ top: number, left: number, maxHeight: number|null,
 *   maxWidth: number|null, side: string, placement: string,
 *   arrow: { x: number }|null }>} `null` while nothing measurable exists.
 */
export async function placeOverlay({
  reference,
  floating,
  kind,
  placement,
  budget = null,
  obstacles,
  options,
} = {}) {
  const roster = resolveKind(kind);
  if (!roster || !reference || !floating) return null;
  if (typeof reference.getBoundingClientRect !== "function") return null;

  const box = floatingBox(floating);
  if (!(box.width > 0) && !(box.height > 0)) return null;

  const merged = { ...roster.options, ...options };

  // The budget runs BEFORE the placement, against the reference alone — and so
  // does the side of a kind with a readable minimum (see the header). Both read
  // the area the chain is bounded by: the viewport, or the call's boundary.
  const boundary = boundaryOf(merged);
  const bounds = boundary ? { boundary } : {};
  const viewport = placementArea(floating, merged);
  const referenceRect = reference.getBoundingClientRect();
  const requestedPlacement = placement ?? roster.placement ?? DEFAULT_PLACEMENT;
  const maxHeight = computeBudget({
    referenceRect,
    viewport,
    budget,
    options: merged,
    placement: requestedPlacement,
  });
  const decidedSide = readableSide(referenceRect, viewport, merged, requestedPlacement);

  // `size` reports the width it found; it never writes to the element. (The
  // library's own docs suggest `Object.assign(element.style, …)` in `apply` —
  // that would make the engine a mutator and break "pure over its inputs".)
  let availableWidth = null;

  const primaryPlacement = decidedSide
    ? onSide(requestedPlacement, decidedSide)
    : requestedPlacement;

  // The main axis is per SIDE (see the header): `gap` everywhere, plus the
  // kind's `flipClearance` on the side opposite the one the call asked for.
  // `flip`'s `reset: { placement }` restarts the chain at this slot, so the
  // function is asked again for each candidate it tries.
  const gap = merged.gap ?? 0;
  const flipClearance = merged.flipClearance ?? 0;
  const flippedSide = OPPOSITE_SIDE[sideOf(requestedPlacement)] ?? null;
  const crossAxis = Number.isFinite(merged.crossGap) ? merged.crossGap : 0;
  const axisOffsets = axisOffsetsOf(merged);
  const offsetValue = (state) => {
    const side = sideOf(state.placement);
    const owedOnFlip = side === flippedSide ? flipClearance : 0;
    // The placement-bound vocabulary, which every kind but the cursor card takes.
    if (!axisOffsets) return { mainAxis: gap + owedOnFlip, crossAxis };

    // The axis-bound one (see the header): a horizontal side spends the X
    // clearance on the main axis and centres on the cross axis.
    const horizontal = side === "left" || side === "right";
    return {
      mainAxis: (horizontal ? axisOffsets.x : axisOffsets.y) + owedOnFlip,
      crossAxis: horizontal ? 0 : axisOffsets.x,
    };
  };

  const flipOptions = { padding: merged.padding, ...bounds };
  if (roster.fallbackPlacements) flipOptions.fallbackPlacements = roster.fallbackPlacements;
  // A decided side is kept: the flip may only trade the alignment on it, so a box
  // too wide for one alignment takes the other rather than crossing the anchor.
  if (decidedSide) {
    const alternative = otherAlignment(primaryPlacement);
    flipOptions.fallbackPlacements = alternative ? [alternative] : [];
  }

  const middleware = [
    offset(offsetValue),
    ...(merged.flip === false ? [] : [flip(flipOptions)]),
    shift({ padding: merged.padding, crossAxis: merged.flip === false, ...bounds }),
    ...(roster.arrow ? [arrowMiddleware({ arrowHalf: merged.arrowHalf })] : []),
    avoidOverlays({
      obstacles,
      padding: merged.padding,
      gap: merged.gap,
      readableHeight: merged.readableHeight,
    }),
    size({
      padding: merged.padding,
      ...bounds,
      apply({ availableWidth: width }) {
        availableWidth = width;
      },
    }),
  ];

  const placed = await computePosition(reference, floating, {
    strategy: "fixed",
    placement: primaryPlacement,
    middleware,
  });

  // `avoidOverlays` reports the candidate it chose through `side` and
  // `maxHeight`. A pass-through — no obstacle hit, or nothing that clears them —
  // reports neither, and the chain's own placement stands, capped at the budget
  // for the side it chose (see the header): the room `avoidOverlays` gives its
  // candidate on that side of the anchor, so a pass-through and an avoided pass
  // never alternate between two caps. A side with no usable room keeps the
  // pre-pass cap rather than going unbounded.
  const avoided = placed.middlewareData.avoidOverlays;
  const arrowData = placed.middlewareData.arrow;
  const side = avoided?.side ?? sideOf(placed.placement);

  return {
    top: placed.y,
    left: placed.x,
    maxHeight: Number.isFinite(avoided?.maxHeight)
      ? avoided.maxHeight
      : (computeBudget({ referenceRect, viewport, budget, options: merged, side }) ?? maxHeight),
    maxWidth: availableWidth == null ? null : Math.max(0, availableWidth),
    side,
    placement: placed.placement,
    arrow: arrowData
      ? {
          x: arrowXFor({
            referenceCenterX: arrowData.referenceCenterX,
            floatingLeft: placed.x,
            floatingWidth: arrowData.floatingWidth,
            arrowHalf: merged.arrowHalf ?? 0,
          }),
        }
      : null,
  };
}

// ---------------------------------------------------------------------------
// KEEPING A PLACEMENT CURRENT
// ---------------------------------------------------------------------------

/** An Element, judged as 1.1.1 judges one: an instance of its own window's `Element`. */
function isElementNode(value) {
  const view = value?.ownerDocument?.defaultView ?? (typeof window === "undefined" ? null : window);
  return Boolean(view?.Element) && value instanceof view.Element;
}

/**
 * The `elementResize` half of `autoUpdate`, observed here rather than by
 * floating-ui (see `subscribeAutoUpdate`): the targets 1.1.1 observes — the
 * reference element, or a virtual reference's `contextElement`, unless
 * `animationFrame` polls the reference instead; and always the floating node —
 * with a re-place on EVERY callback, the first included.
 */
function observeElementResize(reference, floating, onUpdate, { animationFrame }) {
  const observer = new ResizeObserver(() => onUpdate());
  if (!animationFrame) {
    if (isElementNode(reference)) observer.observe(reference);
    else if (reference.contextElement) observer.observe(reference.contextElement);
  }
  observer.observe(floating);
  return () => observer.disconnect();
}

/**
 * Subscribes the kind's `autoUpdate` options and returns the teardown.
 *
 * The options object is spelled OUT by the roster, never defaulted: three of
 * floating-ui's four flags default to TRUE, so a held cursor card's
 * "`elementResize` only" is only true when the other three are written false.
 * A kind with `autoUpdate: false` has no position to keep current and gets a
 * no-op teardown instead of a subscription.
 *
 * `autoUpdate` calls `onUpdate` ONCE immediately, before any event fires — the
 * caller's first placement, not an extra one to guard against.
 *
 * THE ELEMENT'S OWN RESIZE IS OBSERVED HERE, NOT BY FLOATING-UI. 1.1.1's
 * `elementResize` observer drops its FIRST callback (`initialUpdate`), and that
 * callback is the one that reports what the box grew to between the caller's
 * second pass and the first painted frame (a Dossier body resolving from cache in
 * microtasks). Dropped, a box placed while it was short never re-places for that
 * growth. So floating-ui is handed `elementResize: false`, and a kind that
 * declares `elementResize` gets `observeElementResize` instead, over the same
 * targets, re-placing on every callback; the other three flags reach floating-ui
 * exactly as the roster wrote them. A superseded pass is the caller's to drop
 * (its pass token). What keeps the observer from feeding on its own answer is
 * the cap narrowed to the chosen side (see the header): a pass over the box that
 * cap produced lands where the box stands, so nothing resizes again.
 *
 * A virtual reference with no `contextElement` contributes no scroll/resize
 * ancestors of its own; only the floating node's ancestors are listened to.
 *
 * @param {Element|object} reference
 * @param {Element} floating
 * @param {object|string} kind
 * @param {Function} onUpdate
 * @returns {() => void} the teardown.
 */
export function subscribeAutoUpdate(reference, floating, kind, onUpdate) {
  const roster = resolveKind(kind);
  if (!roster || !roster.autoUpdate) return NOOP;
  if (!reference || !floating || typeof onUpdate !== "function") return NOOP;

  const stopAutoUpdate = autoUpdate(reference, floating, onUpdate, {
    ...roster.autoUpdate,
    elementResize: false,
  });
  if (!roster.autoUpdate.elementResize) return stopAutoUpdate;

  const stopObserving = observeElementResize(reference, floating, onUpdate, roster.autoUpdate);
  return () => {
    stopAutoUpdate();
    stopObserving();
  };
}
